import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface Repository {
  id: string; // UUID
  projectId: string | null;
  jiraProjectMappingId: string | null;
  owner: string;
  name: string;
  description: string | null;
  primaryLanguage: string | null;
  stars: number;
  size: number;
  defaultBranch: string;
  isActive: boolean;
  settings: Record<string, any> | null;
  prReviewEnabled: boolean | null;
  prReviewMaxIterations: number | null;
  prReviewAiModelId: string | null;
  prReviewAllowedBranchPrefixes: string[] | null;
  prReviewFixBranchPrefix: string | null;
  prReviewAiModel?: {
    id: string;
    code: string;
    displayName: string;
    provider: {
      id: string;
      code: string;
      displayName: string;
    };
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface RepositoriesState {
  repositories: Repository[];
  orgRepositories: Repository[]; // All repositories in the organization (for checking existing repos)
  currentRepository: Repository | null;
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: RepositoriesState = {
  repositories: [],
  orgRepositories: [],
  currentRepository: null,
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getRepositories = createAsyncThunk(
  'repositories/getRepositories',
  async (params: { projectId?: string; organizationId?: string; slim?: boolean; forOrgLookup?: boolean }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.projectId) queryParams.append('projectId', params.projectId);
      if (params.organizationId) queryParams.append('organizationId', params.organizationId);
      if (params.slim) queryParams.append('slim', 'true');

      const queryString = queryParams.toString();
      const url = queryString ? `/repositories?${queryString}` : '/repositories';

      const response = await apiRequest(url);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);


export const createRepository = createAsyncThunk(
  'repositories/createRepository',
  async (
    repositoryData: {
      projectId: string;
      owner: string;
      name: string;
      description?: string;
      primaryLanguage?: string;
      defaultBranch?: string;
      source?: 'github' | 'gitlab';
      externalId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest('/repositories', {
        method: 'POST',
        body: JSON.stringify(repositoryData),
      });
      return response;
    } catch (error: any) {
      // Pass full error object for plan limit detection
      // API returns: { error: { code: 'PLAN_LIMIT_EXCEEDED', message: '...' } }
      if (error?.error?.code === 'PLAN_LIMIT_EXCEEDED') {
        return rejectWithValue(error);
      }
      // Use generic error message for security - don't expose internal details
      console.error('Create repository error:', error);
      return rejectWithValue('Something went wrong while adding the repository. Please try again.');
    }
  }
);

export const getRepository = createAsyncThunk(
  'repositories/getRepository',
  async (repositoryId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/repositories/${repositoryId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateRepository = createAsyncThunk(
  'repositories/updateRepository',
  async (
    {
      repositoryId,
      data,
    }: {
      repositoryId: string;
      data: {
        prReviewEnabled?: boolean;
        prReviewMaxIterations?: number;
        prReviewAiModelId?: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(`/repositories/${repositoryId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRepository = createAsyncThunk(
  'repositories/deleteRepository',
  async ({ repositoryId, projectId }: { repositoryId: string; projectId?: string }, { rejectWithValue }) => {
    try {
      const url = projectId
        ? `/repositories/${repositoryId}?projectId=${projectId}`
        : `/repositories/${repositoryId}`;

      await apiRequest(url, {
        method: 'DELETE',
      });
      return repositoryId;
    } catch (error: any) {
      return rejectWithValue('Something went wrong while deleting the repository. Please try again.');
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get repositories - routes to repositories or orgRepositories based on params
  [getRepositories.pending.type]: (state: RepositoriesState, action: any) => {
    state.status = 'loading';
    const params = action.meta?.arg || {};
    if (params.forOrgLookup) {
      state.orgRepositories = [];
    } else {
      state.repositories = [];
    }
  },
  [getRepositories.fulfilled.type]: (state: RepositoriesState, action: any) => {
    const repos = !action.payload
      ? []
      : Array.isArray(action.payload.response)
        ? action.payload.response
        : action.payload.response?.repositories || action.payload.repositories || [];

    // Route based on params: forOrgLookup → orgRepositories, everything else → repositories
    const params = action.meta?.arg || {};
    if (params.forOrgLookup) {
      state.orgRepositories = repos;
    } else {
      state.repositories = repos;
    }

    state.status = 'success';
    state.error = '';
  },
  [getRepositories.rejected.type]: (state: RepositoriesState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch repositories';
  },

  // Create repository
  [createRepository.pending.type]: { status: 'loading' as const },
  [createRepository.fulfilled.type]: (state: RepositoriesState, action: any) => {
    const newRepository = action.payload.response || action.payload;
    state.repositories.push(newRepository);
    state.status = 'success';
    state.error = '';
  },
  [createRepository.rejected.type]: (state: RepositoriesState, action: any) => {
    state.status = 'failed';
    // Handle plan limit error - don't set error message as it will be handled by dialog
    if (action.payload?.error?.code === 'PLAN_LIMIT_EXCEEDED') {
      state.error = '';
      return;
    }
    state.error = typeof action.payload === 'string' ? action.payload : action.payload?.message || 'Failed to create repository';
  },

  // Get single repository
  [getRepository.pending.type]: { status: 'loading' as const },
  [getRepository.fulfilled.type]: (state: RepositoriesState, action: any) => {
    // Handle null response (404 - repository not found)
    if (!action.payload) {
      state.currentRepository = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    const repository = action.payload.response || action.payload;
    state.currentRepository = repository;
    // Also update in repositories array if exists
    const index = state.repositories.findIndex((r) => r.id === repository.id);
    if (index !== -1) {
      state.repositories[index] = repository;
    }
    state.status = 'success';
    state.error = '';
  },
  [getRepository.rejected.type]: (state: RepositoriesState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch repository';
  },

  // Update repository
  [updateRepository.pending.type]: { status: 'loading' as const },
  [updateRepository.fulfilled.type]: (state: RepositoriesState, action: any) => {
    const updatedRepository = action.payload.response || action.payload;

    // Only update if currentRepository exists and IDs match
    if (state.currentRepository && state.currentRepository.id === updatedRepository.id) {
      // Check if any PR review fields actually changed
      const prFieldsChanged =
        state.currentRepository.prReviewEnabled !== updatedRepository.prReviewEnabled ||
        state.currentRepository.prReviewMaxIterations !== updatedRepository.prReviewMaxIterations ||
        state.currentRepository.prReviewAiModelId !== updatedRepository.prReviewAiModelId;

      // Only create new object if data actually changed (prevents unnecessary re-renders)
      if (prFieldsChanged) {
        state.currentRepository = {
          ...state.currentRepository,
          ...updatedRepository
        };
      }
    }

    // Update in repositories array
    const index = state.repositories.findIndex((r) => r.id === updatedRepository.id);
    if (index !== -1) {
      const prFieldsChanged =
        state.repositories[index].prReviewEnabled !== updatedRepository.prReviewEnabled ||
        state.repositories[index].prReviewMaxIterations !== updatedRepository.prReviewMaxIterations ||
        state.repositories[index].prReviewAiModelId !== updatedRepository.prReviewAiModelId;

      if (prFieldsChanged) {
        state.repositories[index] = {
          ...state.repositories[index],
          ...updatedRepository
        };
      }
    }
    state.status = 'success';
    state.error = '';
  },
  [updateRepository.rejected.type]: (state: RepositoriesState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to update repository';
  },

  // Delete repository
  [deleteRepository.pending.type]: { status: 'loading' as const },
  [deleteRepository.fulfilled.type]: (state: RepositoriesState, action: any) => {
    const repositoryId = action.payload;
    state.repositories = state.repositories.filter((r) => r.id !== repositoryId);
    if (state.currentRepository?.id === repositoryId) {
      state.currentRepository = null;
    }
    state.status = 'success';
    state.error = '';
  },
  [deleteRepository.rejected.type]: (state: RepositoriesState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to delete repository';
  },
};

// ==============================|| SLICE ||============================== //

export const repositoriesSlice = createSlice({
  name: 'repositories',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentRepository(state, action) {
      state.currentRepository = action.payload;
    },
    clearRepositoriesState(state) {
      state.repositories = [];
      state.orgRepositories = [];
      state.currentRepository = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentRepository, clearRepositoriesState } = repositoriesSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectRepositories = (state: any) => state.repositories.repositories;
export const selectOrgRepositories = (state: any) => state.repositories.orgRepositories;
export const selectCurrentRepository = (state: any) => state.repositories.currentRepository;
export const selectRepositoriesStatus = (state: any) => state.repositories.status;
export const selectRepositoriesError = (state: any) => state.repositories.error;

export default repositoriesSlice.reducer;
